import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const ORIGENS = ['Google Ads', 'Meta Ads', 'Indicação', 'Prospecção Própria']

function HistoricoDeal({ dealId }) {
    const [historico, setHistorico] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        api.get('/deal-activities', { params: { deal_id: dealId } }).then(r => {
            setHistorico(r.data)
            setLoading(false)
        })
    }, [dealId])

    if (loading) return <p className="text-xs text-gray-500 mt-2">Carregando histórico...</p>
    if (historico.length === 0) return <p className="text-xs text-gray-500 mt-2">Nenhuma atividade registrada.</p>

    return (
        <div className="mt-3 pt-3 border-t border-gray-700">
            <p className="text-xs text-gray-500 mb-2 font-semibold">📋 Histórico</p>
            <div className="space-y-2">
                {historico.map(h => (
                    <div key={h.id} className="flex items-start gap-2">
                        <div className="flex-1">
                            <p className="text-xs text-gray-300">{h.descricao}</p>
                            <p className="text-xs text-gray-500">
                                {h.user_name && <span className="text-gray-400">{h.user_name} · </span>}
                                {new Date(h.created_at).toLocaleDateString('pt-BR')} {new Date(h.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function DealCard({ deal, index, onDelete, onStatusChange, onEdit, tipoPipeline }) {
    const [expanded, setExpanded] = useState(false)
    const [showMotivoPerda, setShowMotivoPerda] = useState(false)
    const [motivo, setMotivo] = useState('')

    const confirmarPerda = () => {
        onStatusChange(deal.id, 'lost', motivo)
        setShowMotivoPerda(false)
        setMotivo('')
    }

    return (
        <Draggable draggableId={deal.id} index={index}>
            {(provided) => (
                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                    className="bg-gray-800 rounded-lg p-3 mb-2 border border-gray-700 hover:border-indigo-500 transition cursor-grab">
                    <div className="flex justify-between items-start">
                        <p className="font-medium text-sm text-white cursor-pointer flex-1" onClick={() => setExpanded(!expanded)}>
                            {deal.title}
                            <span className="ml-2 text-gray-500 text-xs">{expanded ? '▲' : '▼'}</span>
                        </p>
                        <div className="flex items-center gap-1 ml-2">
                            <button onClick={(e) => { e.stopPropagation(); onEdit(deal) }} className="text-gray-600 hover:text-indigo-400 text-xs transition" title="Editar">✏️</button>
                            <button onClick={(e) => { e.stopPropagation(); onDelete(deal.id) }} className="text-gray-600 hover:text-red-400 text-xs transition" title="Excluir">✕</button>
                        </div>
                    </div>
                    {deal.criado_por && (
                        <p className="text-xs text-gray-500 mt-1">🧑‍💼 <span className="text-gray-400">{deal.criado_por}</span></p>
                    )}
                    {deal.produto && <span className="text-xs text-indigo-300 bg-indigo-900/40 px-2 py-0.5 rounded-full mt-1 inline-block">{deal.produto}</span>}
                    {deal.origem && <span className="text-xs text-yellow-300 bg-yellow-900/40 px-2 py-0.5 rounded-full mt-1 ml-1 inline-block">{deal.origem}</span>}
                    <div className="flex justify-between items-center mt-2">
                        <span className="text-emerald-400 text-xs font-semibold">
                            {deal.value ? `R$ ${Number(deal.value).toLocaleString('pt-BR')}` : '—'}
                        </span>
                        <span className="text-gray-500 text-xs">📅 {new Date(deal.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                    {deal.data_agendamento && (
                        <div className="mt-1">
                            <span className="text-xs text-cyan-400 bg-cyan-900/30 px-2 py-0.5 rounded-full">
                                🗓️ Agendado: {new Date(deal.data_agendamento + 'T12:00:00').toLocaleDateString('pt-BR')}
                            </span>
                        </div>
                    )}
                    {deal.localizacao && (
                        <div className="mt-1">
                            <span className="text-xs text-orange-300 bg-orange-900/30 px-2 py-0.5 rounded-full">
                                📍 {deal.localizacao}
                            </span>
                        </div>
                    )}
                    {expanded && (
                        <div className="mt-3 pt-3 border-t border-gray-700 space-y-1">
                            {deal.segmento && <p className="text-xs text-gray-400">🏷️ <span className="text-gray-300">{deal.segmento}</span></p>}
                            {deal.contato && <p className="text-xs text-gray-400">👤 <span className="text-gray-300">{deal.contato}</span></p>}
                            {deal.telefone && <p className="text-xs text-gray-400">📱 <span className="text-gray-300">{deal.telefone}</span></p>}
                            {deal.observacoes && (
                                <div className="mt-2 bg-gray-900/60 rounded-lg p-2">
                                    <p className="text-xs text-gray-500 mb-1">📝 Observações</p>
                                    <p className="text-xs text-gray-300 whitespace-pre-wrap">{deal.observacoes}</p>
                                </div>
                            )}
                            <HistoricoDeal dealId={deal.id} />
                        </div>
                    )}
                    <div className="flex gap-2 mt-3">
                        {tipoPipeline === 'pavimentacao' ? (
                            <>
                                <button onClick={(e) => { e.stopPropagation(); onStatusChange(deal.id, 'won') }}
                                    className="flex-1 bg-emerald-700/40 hover:bg-emerald-600 text-emerald-300 hover:text-white text-xs py-1 rounded-lg transition font-semibold">✓ Ganho</button>
                                <button onClick={(e) => { e.stopPropagation(); onStatusChange(deal.id, 'in_progress') }}
                                    className="flex-1 bg-blue-700/40 hover:bg-blue-600 text-blue-300 hover:text-white text-xs py-1 rounded-lg transition font-semibold">🚧 Andamento</button>
                                <button onClick={(e) => { e.stopPropagation(); onStatusChange(deal.id, 'closed') }}
                                    className="flex-1 bg-orange-700/40 hover:bg-orange-600 text-orange-300 hover:text-white text-xs py-1 rounded-lg transition font-semibold">🏗️ Obra</button>
                                <button onClick={(e) => { e.stopPropagation(); setShowMotivoPerda(true) }}
                                    className="flex-1 bg-red-700/40 hover:bg-red-600 text-red-300 hover:text-white text-xs py-1 rounded-lg transition font-semibold">✕ Perdido</button>
                            </>
                        ) : (
                            <>
                                <button onClick={(e) => { e.stopPropagation(); onStatusChange(deal.id, 'won') }}
                                    className="flex-1 bg-emerald-700/40 hover:bg-emerald-600 text-emerald-300 hover:text-white text-xs py-1 rounded-lg transition font-semibold">✓ Ganho</button>
                                <button onClick={(e) => { e.stopPropagation(); setShowMotivoPerda(true) }}
                                    className="flex-1 bg-red-700/40 hover:bg-red-600 text-red-300 hover:text-white text-xs py-1 rounded-lg transition font-semibold">✕ Perdido</button>
                            </>
                        )}
                    </div>
                    {showMotivoPerda && (
                        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
                            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-red-800 shadow-2xl">
                                <h2 className="text-lg font-bold mb-2 text-red-400">❌ Motivo da Perda</h2>
                                <p className="text-gray-400 text-sm mb-4">Informe o motivo pelo qual o negócio <span className="text-white font-semibold">"{deal.title}"</span> foi perdido.</p>
                                <textarea className="w-full bg-gray-800 rounded-lg px-3 py-2 mb-4 text-sm outline-none focus:ring-2 ring-red-500 resize-none h-24"
                                    placeholder="Ex: Preço alto, concorrente, cliente desistiu..." value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus />
                                <div className="flex gap-2">
                                    <button onClick={confirmarPerda} className="flex-1 bg-red-600 hover:bg-red-700 py-2 rounded-lg text-sm font-semibold transition">Confirmar Perda</button>
                                    <button onClick={() => { setShowMotivoPerda(false); setMotivo('') }} className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-sm transition">Cancelar</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Draggable>
    )
}

function ObrasAndamentoView({ deals, onStatusChange, busca, onEdit }) {
    const inProgressDeals = deals.filter(d => {
        if (d.status !== 'in_progress') return false
        if (busca?.trim()) return d.title?.toLowerCase().includes(busca.toLowerCase())
        return true
    })
    const totalAndamento = inProgressDeals.reduce((s, d) => s + Number(d.value || 0), 0)

    if (inProgressDeals.length === 0) return (
        <div className="text-center py-16 text-gray-500">
            <p className="text-4xl mb-3">🚧</p>
            <p>{busca ? `Nenhuma obra encontrada para "${busca}".` : 'Nenhuma obra em andamento.'}</p>
            <p className="text-sm mt-1">{!busca && 'Marque negócios como "Em Andamento" no Pipeline!'}</p>
        </div>
    )

    return (
        <div>
            <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-4 mb-6 flex items-center justify-between">
                <div>
                    <p className="text-blue-400 text-sm font-semibold">Total em Andamento</p>
                    <p className="text-2xl font-bold text-blue-300">R$ {totalAndamento.toLocaleString('pt-BR')}</p>
                </div>
                <div className="text-right">
                    <p className="text-gray-400 text-sm">{inProgressDeals.length} obras</p>
                    <p className="text-xs text-gray-500">em execução</p>
                </div>
            </div>
            <div className="space-y-3">
                {inProgressDeals.map(deal => (
                    <div key={deal.id} className="bg-gray-900 rounded-xl p-4 border border-blue-900/40 hover:border-blue-700/50 transition">
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-blue-400 text-xs font-bold">🚧 EM ANDAMENTO</span>
                                    {deal.produto && <span className="text-xs text-indigo-300 bg-indigo-900/40 px-2 py-0.5 rounded-full">{deal.produto}</span>}
                                    {deal.origem && <span className="text-xs text-yellow-300 bg-yellow-900/40 px-2 py-0.5 rounded-full">{deal.origem}</span>}
                                </div>
                                <p className="font-semibold text-white">{deal.title}</p>
                                <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
                                    {deal.segmento && <span>🏷️ {deal.segmento}</span>}
                                    {deal.contato && <span>👤 {deal.contato}</span>}
                                    {deal.telefone && <span>📱 {deal.telefone}</span>}
                                    {deal.localizacao && <span className="text-orange-300">📍 {deal.localizacao}</span>}
                                    {deal.data_agendamento && <span className="text-cyan-400">🗓️ {new Date(deal.data_agendamento + 'T12:00:00').toLocaleDateString('pt-BR')}</span>}
                                </div>
                                {deal.observacoes && <p className="text-xs text-gray-500 mt-2 bg-gray-800 rounded-lg px-3 py-2">📝 {deal.observacoes}</p>}
                            </div>
                            <div className="text-right ml-4 flex flex-col items-end gap-2">
                                <button onClick={() => onEdit(deal)} className="text-gray-500 hover:text-indigo-400 transition text-sm" title="Editar">✏️ Editar</button>
                                <p className="text-blue-400 font-bold text-lg">{deal.value ? `R$ ${Number(deal.value).toLocaleString('pt-BR')}` : '—'}</p>
                                <p className="text-xs text-gray-500">{new Date(deal.updated_at).toLocaleDateString('pt-BR')}</p>
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-800 flex gap-2">
                            <button onClick={() => onStatusChange(deal.id, 'closed')}
                                className="flex-1 bg-orange-700/40 hover:bg-orange-600 text-orange-300 hover:text-white text-xs py-1.5 rounded-lg transition font-semibold">
                                🏗️ Obra Fechada
                            </button>
                            <button onClick={() => onStatusChange(deal.id, 'won')}
                                className="flex-1 bg-emerald-700/40 hover:bg-emerald-600 text-emerald-300 hover:text-white text-xs py-1.5 rounded-lg transition font-semibold">
                                ✓ Confirmar Pagamento (Ganho)
                            </button>
                            <button onClick={() => onStatusChange(deal.id, 'open')}
                                className="flex-1 bg-gray-700/60 hover:bg-gray-600 text-gray-300 hover:text-white text-xs py-1.5 rounded-lg transition font-semibold">
                                ↩ Reabrir no Pipeline
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function ObrasView({ deals, onStatusChange, onReabrir, busca }) {
    const closedDeals = deals.filter(d => {
        if (d.status !== 'closed') return false
        if (busca?.trim()) return d.title?.toLowerCase().includes(busca.toLowerCase())
        return true
    })
    const totalObras = closedDeals.reduce((s, d) => s + Number(d.value || 0), 0)

    if (closedDeals.length === 0) return (
        <div className="text-center py-16 text-gray-500">
            <p className="text-4xl mb-3">🏗️</p>
            <p>{busca ? `Nenhuma obra encontrada para "${busca}".` : 'Nenhuma obra fechada ainda.'}</p>
            <p className="text-sm mt-1">{!busca && 'Marque negócios como "Obra Fechada" no Pipeline!'}</p>
        </div>
    )

    return (
        <div>
            <div className="bg-orange-900/20 border border-orange-800 rounded-xl p-4 mb-6 flex items-center justify-between">
                <div>
                    <p className="text-orange-400 text-sm font-semibold">Total em Obras</p>
                    <p className="text-2xl font-bold text-orange-300">R$ {totalObras.toLocaleString('pt-BR')}</p>
                </div>
                <div className="text-right">
                    <p className="text-gray-400 text-sm">{closedDeals.length} obras</p>
                    <p className="text-xs text-gray-500">aguardando pagamento</p>
                </div>
            </div>
            <div className="space-y-3">
                {closedDeals.map(deal => (
                    <div key={deal.id} className="bg-gray-900 rounded-xl p-4 border border-orange-900/40 hover:border-orange-700/50 transition">
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-orange-400 text-xs font-bold">🏗️ OBRA FECHADA</span>
                                    {deal.produto && <span className="text-xs text-indigo-300 bg-indigo-900/40 px-2 py-0.5 rounded-full">{deal.produto}</span>}
                                    {deal.origem && <span className="text-xs text-yellow-300 bg-yellow-900/40 px-2 py-0.5 rounded-full">{deal.origem}</span>}
                                </div>
                                <p className="font-semibold text-white">{deal.title}</p>
                                <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
                                    {deal.segmento && <span>🏷️ {deal.segmento}</span>}
                                    {deal.contato && <span>👤 {deal.contato}</span>}
                                    {deal.telefone && <span>📱 {deal.telefone}</span>}
                                    {deal.localizacao && <span className="text-orange-300">📍 {deal.localizacao}</span>}
                                    {deal.data_agendamento && <span className="text-cyan-400">🗓️ {new Date(deal.data_agendamento + 'T12:00:00').toLocaleDateString('pt-BR')}</span>}
                                </div>
                                {deal.observacoes && <p className="text-xs text-gray-500 mt-2 bg-gray-800 rounded-lg px-3 py-2">📝 {deal.observacoes}</p>}
                            </div>
                            <div className="text-right ml-4">
                                <p className="text-orange-400 font-bold text-lg">{deal.value ? `R$ ${Number(deal.value).toLocaleString('pt-BR')}` : '—'}</p>
                                <p className="text-xs text-gray-500 mt-1">{new Date(deal.updated_at).toLocaleDateString('pt-BR')}</p>
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-800 flex gap-2">
                            <button onClick={() => onStatusChange(deal.id, 'in_progress')}
                                className="flex-1 bg-blue-700/40 hover:bg-blue-600 text-blue-300 hover:text-white text-xs py-1.5 rounded-lg transition font-semibold">
                                🚧 Obra em Andamento
                            </button>
                            <button onClick={() => onStatusChange(deal.id, 'won')}
                                className="flex-1 bg-emerald-700/40 hover:bg-emerald-600 text-emerald-300 hover:text-white text-xs py-1.5 rounded-lg transition font-semibold">
                                ✓ Confirmar Pagamento (Ganho)
                            </button>
                            <button onClick={() => onReabrir(deal.id)}
                                className="flex-1 bg-gray-700/60 hover:bg-gray-600 text-gray-300 hover:text-white text-xs py-1.5 rounded-lg transition font-semibold">
                                ↩ Reabrir no Pipeline
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function GanhosView({ deals, onStatusChange, mesGanhos, setMesGanhos }) {
    const wonDeals = deals.filter(d => {
        if (d.status !== 'won') return false
        if (mesGanhos) {
            const mes = new Date(d.updated_at).toISOString().slice(0, 7)
            return mes === mesGanhos
        }
        return true
    })
    const totalGanho = wonDeals.reduce((s, d) => s + Number(d.value || 0), 0)

    if (deals.filter(d => d.status === 'won').length === 0) return (
        <div className="text-center py-16 text-gray-500">
            <p className="text-4xl mb-3">🏆</p>
            <p>Nenhum negócio ganho ainda.</p>
            <p className="text-sm mt-1">Marque negócios como ganhos no Pipeline!</p>
        </div>
    )

    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <input type="month" value={mesGanhos} onChange={e => setMesGanhos(e.target.value)}
                    className="bg-gray-800 text-sm rounded-lg px-3 py-1.5 outline-none focus:ring-2 ring-indigo-500" />
                {mesGanhos && <button onClick={() => setMesGanhos('')} className="text-xs text-gray-500 hover:text-white transition">✕ Todos</button>}
                {mesGanhos && <span className="text-xs text-emerald-400">{wonDeals.length} negócio{wonDeals.length !== 1 ? 's' : ''} no mês</span>}
            </div>
            <div className="bg-emerald-900/20 border border-emerald-800 rounded-xl p-4 mb-6 flex items-center justify-between">
                <div>
                    <p className="text-emerald-400 text-sm font-semibold">Total Ganho</p>
                    <p className="text-2xl font-bold text-emerald-300">R$ {totalGanho.toLocaleString('pt-BR')}</p>
                </div>
                <div className="text-right">
                    <p className="text-gray-400 text-sm">{wonDeals.length} negócios</p>
                    <p className="text-xs text-gray-500">convertidos</p>
                </div>
            </div>
            <div className="space-y-3">
                {wonDeals.map(deal => (
                    <div key={deal.id} className="bg-gray-900 rounded-xl p-4 border border-emerald-900/40 hover:border-emerald-700/50 transition">
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-emerald-400 text-xs font-bold">✓ GANHO</span>
                                    {deal.produto && <span className="text-xs text-indigo-300 bg-indigo-900/40 px-2 py-0.5 rounded-full">{deal.produto}</span>}
                                    {deal.origem && <span className="text-xs text-yellow-300 bg-yellow-900/40 px-2 py-0.5 rounded-full">{deal.origem}</span>}
                                </div>
                                <p className="font-semibold text-white">{deal.title}</p>
                                <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
                                    {deal.segmento && <span>🏷️ {deal.segmento}</span>}
                                    {deal.contato && <span>👤 {deal.contato}</span>}
                                    {deal.telefone && <span>📱 {deal.telefone}</span>}
                                    {deal.localizacao && <span className="text-orange-300">📍 {deal.localizacao}</span>}
                                    {deal.data_agendamento && <span className="text-cyan-400">🗓️ {new Date(deal.data_agendamento + 'T12:00:00').toLocaleDateString('pt-BR')}</span>}
                                </div>
                                {deal.observacoes && <p className="text-xs text-gray-500 mt-2 bg-gray-800 rounded-lg px-3 py-2">📝 {deal.observacoes}</p>}
                            </div>
                            <div className="text-right ml-4">
                                <p className="text-emerald-400 font-bold text-lg">{deal.value ? `R$ ${Number(deal.value).toLocaleString('pt-BR')}` : '—'}</p>
                                <p className="text-xs text-gray-500 mt-1">{new Date(deal.updated_at).toLocaleDateString('pt-BR')}</p>
                            </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-800">
                            <button onClick={() => onStatusChange(deal.id, 'open')}
                                className="w-full bg-gray-700/60 hover:bg-gray-600 text-gray-300 hover:text-white text-xs py-1.5 rounded-lg transition font-semibold">
                                ↩ Reabrir no Pipeline
                            </button>
                        </div>
                    </div>
                ))}
                {wonDeals.length === 0 && mesGanhos && (
                    <p className="text-gray-500 text-center py-8">Nenhum negócio ganho neste mês.</p>
                )}
            </div>
        </div>
    )
}

function ConfigurarPipeline({ stages, companyId, onClose, onUpdate }) {
    const [lista, setLista] = useState(stages.map(s => ({ ...s })))
    const [novaEtapa, setNovaEtapa] = useState({ name: '', color: '#6366f1' })
    const [salvando, setSalvando] = useState(false)
    const [msg, setMsg] = useState('')

    const handleRenomear = (stageId, name) => setLista(prev => prev.map(s => s.id === stageId ? { ...s, name } : s))
    const handleCor = (stageId, color) => setLista(prev => prev.map(s => s.id === stageId ? { ...s, color } : s))

    const handleSalvar = async (stage) => {
        setSalvando(true)
        await api.put(`/stages/${stage.id}`, { name: stage.name, color: stage.color })
        setMsg('Salvo!')
        setTimeout(() => setMsg(''), 2000)
        setSalvando(false)
        onUpdate()
    }

    const handleAdicionar = async () => {
        if (!novaEtapa.name) return
        const maxPos = lista.length > 0 ? Math.max(...lista.map(s => s.position)) : 0
        const { data } = await api.post('/stages', {
            name: novaEtapa.name,
            color: novaEtapa.color,
            position: maxPos + 1,
            company_id: companyId
        })
        setLista(prev => [...prev, data])
        setNovaEtapa({ name: '', color: '#6366f1' })
        onUpdate()
    }

    const handleExcluir = async (stageId) => {
        if (!window.confirm('Excluir esta etapa? Os deals nela ficarão sem etapa.')) return
        await api.delete(`/stages/${stageId}`)
        setLista(prev => prev.filter(s => s.id !== stageId))
        onUpdate()
    }

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-lg border border-gray-700 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold">⚙️ Configurar Etapas do Pipeline</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-white">✕</button>
                </div>
                {msg && <p className="text-emerald-400 text-xs mb-3">{msg}</p>}
                <div className="space-y-3 mb-6">
                    {lista.map(stage => (
                        <div key={stage.id} className="bg-gray-800 rounded-lg p-3 flex items-center gap-3">
                            <input type="color" value={stage.color} onChange={e => handleCor(stage.id, e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" />
                            <input className="flex-1 bg-gray-700 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 ring-indigo-500"
                                value={stage.name} onChange={e => handleRenomear(stage.id, e.target.value)} />
                            <button onClick={() => handleSalvar(stage)} disabled={salvando}
                                className="text-xs bg-indigo-600 hover:bg-indigo-700 px-2 py-1.5 rounded-lg transition whitespace-nowrap">💾 Salvar</button>
                            <button onClick={() => handleExcluir(stage.id)}
                                className="text-gray-600 hover:text-red-400 transition text-lg">🗑️</button>
                        </div>
                    ))}
                </div>
                <div className="border-t border-gray-700 pt-4">
                    <p className="text-sm text-gray-400 mb-3 font-semibold">+ Nova Etapa</p>
                    <div className="flex items-center gap-3">
                        <input type="color" value={novaEtapa.color} onChange={e => setNovaEtapa(prev => ({ ...prev, color: e.target.value }))}
                            className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" />
                        <input className="flex-1 bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 ring-indigo-500"
                            placeholder="Nome da etapa" value={novaEtapa.name}
                            onChange={e => setNovaEtapa(prev => ({ ...prev, name: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && handleAdicionar()} />
                        <button onClick={handleAdicionar}
                            className="bg-indigo-600 hover:bg-indigo-700 px-3 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap">Adicionar</button>
                    </div>
                </div>
                <button onClick={onClose} className="w-full mt-6 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-sm transition">Fechar</button>
            </div>
        </div>
    )
}

const emptyForm = { title: '', value: '', stage_id: '', produto: '', origem: '', segmento: '', contato: '', telefone: '', observacoes: '', data_agendamento: '', localizacao: '' }

function FormNegocio({ title, form, setForm, onSave, onCancel, stages, produtos, contatos, saveLabel = 'Criar' }) {
    const inputClass = "w-full bg-gray-800 rounded-lg px-3 py-2 mb-3 text-sm outline-none focus:ring-2 ring-indigo-500"
    const f = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }))
    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
            <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-gray-700 max-h-[90vh] overflow-y-auto">
                <h2 className="text-lg font-bold mb-4">{title}</h2>
                <input className={inputClass} placeholder="Título *" value={form.title} onChange={f('title')} />
                <input className={inputClass} placeholder="Valor (R$)" type="number" value={form.value} onChange={f('value')} />
                <select className={inputClass} value={form.stage_id} onChange={f('stage_id')}>
                    <option value="">Selecione a etapa *</option>
                    {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select className={inputClass} value={form.produto} onChange={f('produto')}>
                    <option value="">Produto / Serviço</option>
                    {produtos.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
                <select className={inputClass} value={form.origem} onChange={f('origem')}>
                    <option value="">Origem do Lead</option>
                    {ORIGENS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <input className={inputClass} placeholder="Segmento do cliente" value={form.segmento} onChange={f('segmento')} />
                {contatos.length > 0 ? (
                    <select className={inputClass} value={form.contato} onChange={f('contato')}>
                        <option value="">Selecione um contato</option>
                        {contatos.map(c => <option key={c.id} value={c.name}>{c.name}{c.role ? ` — ${c.role}` : ''}</option>)}
                        <option value="__outro__">✏️ Digitar manualmente</option>
                    </select>
                ) : (
                    <input className={inputClass} placeholder="Contato" value={form.contato} onChange={f('contato')} />
                )}
                {form.contato === '__outro__' && (
                    <input className={inputClass} placeholder="Digite o contato" onChange={e => setForm(prev => ({ ...prev, contato: e.target.value }))} autoFocus />
                )}
                <input className={inputClass} placeholder="📱 Telefone" value={form.telefone} onChange={f('telefone')} />
                <input className={inputClass} placeholder="📍 Localização" value={form.localizacao} onChange={f('localizacao')} />
                <label className="text-xs text-gray-400 mb-1 block">🗓️ Data de Agendamento</label>
                <input className={inputClass} type="date" value={form.data_agendamento} onChange={f('data_agendamento')} />
                <textarea className={inputClass + " resize-none h-24"} placeholder="Observações" value={form.observacoes} onChange={f('observacoes')} />
                <div className="flex gap-2 mt-1">
                    <button onClick={onSave} className="flex-1 bg-indigo-600 hover:bg-indigo-700 py-2 rounded-lg text-sm font-semibold transition">{saveLabel}</button>
                    <button onClick={onCancel} className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-sm transition">Cancelar</button>
                </div>
            </div>
        </div>
    )
}

export default function Pipeline() {
    const { user, profile } = useAuth()
    const isMaster = profile?.user_role === 'master'
    const location = useLocation()

    const [aba, setAba] = useState('pipeline')
    const [stages, setStages] = useState([])
    const [deals, setDeals] = useState([])
    const [showForm, setShowForm] = useState(false)
    const [form, setForm] = useState(emptyForm)
    const [empresas, setEmpresas] = useState([])
    const [empresaSelecionada, setEmpresaSelecionada] = useState(null)
    const [produtos, setProdutos] = useState([])
    const [contatos, setContatos] = useState([])
    const [editingDeal, setEditingDeal] = useState(null)
    const [editForm, setEditForm] = useState(emptyForm)
    const [showConfig, setShowConfig] = useState(false)
    const [filtroDataDe, setFiltroDataDe] = useState('')
    const [filtroDataAte, setFiltroDataAte] = useState('')
    const [showFiltro, setShowFiltro] = useState(false)
    const [tipoPipeline, setTipoPipeline] = useState('default')
    const [busca, setBusca] = useState('')
    const [mesGanhos, setMesGanhos] = useState('')

    const getCompanyId = () => isMaster ? empresaSelecionada : profile?.company_id

    const carregarStages = () => {
        const cId = getCompanyId()
        if (!cId) {
            api.get('/stages').then(r => setStages(r.data))
        } else {
            api.get('/stages', { params: { company_id: cId } }).then(r => setStages(r.data))
        }
    }

    useEffect(() => {
        const cId = getCompanyId()
        if (!cId) { setTipoPipeline('default'); return }
        const empresa = empresas.find(e => e.id === cId)
        if (empresa) {
            setTipoPipeline(empresa.tipo_pipeline || 'default')
        } else if (!isMaster) {
            api.get(`/companies/${cId}`).then(r => {
                setTipoPipeline(r.data.tipo_pipeline || 'default')
            }).catch(() => setTipoPipeline('default'))
        }
    }, [empresaSelecionada, empresas, profile]) // eslint-disable-line

    useEffect(() => {
        carregarStages()
        if (isMaster) api.get('/companies').then(r => setEmpresas(r.data))
    }, [isMaster, profile, empresaSelecionada]) // eslint-disable-line

    useEffect(() => {
        if (!user) return
        const params = {}
        if (isMaster) {
            params.is_master = true
            if (empresaSelecionada) params.company_id = empresaSelecionada
        } else {
            params.is_master = false
            params.company_id = profile?.company_id
        }
        api.get('/deals', { params }).then(r => setDeals(r.data))
    }, [user, isMaster, empresaSelecionada, profile]) // eslint-disable-line

    useEffect(() => {
        const cId = getCompanyId()
        if (!cId) { setProdutos([]); setContatos([]); return }
        api.get(`/products/${cId}`).then(r => setProdutos(r.data))
        api.get('/contacts', { params: { company_id: cId } }).then(r => setContatos(r.data)).catch(() => setContatos([]))
    }, [empresaSelecionada, profile, isMaster]) // eslint-disable-line

    useEffect(() => {
        if (!location.state?.openDealId || deals.length === 0) return
        const deal = deals.find(d => d.id === location.state.openDealId)
        if (deal) {
            handleOpenEdit(deal)
            window.history.replaceState({}, '')
        }
    }, [location.state, deals]) // eslint-disable-line

    const filtrarPorData = (lista) => {
        if (!filtroDataDe && !filtroDataAte) return lista
        return lista.filter(d => {
            const criado = new Date(d.created_at)
            criado.setHours(0, 0, 0, 0)
            if (filtroDataDe) {
                const de = new Date(filtroDataDe + 'T00:00:00')
                if (criado < de) return false
            }
            if (filtroDataAte) {
                const ate = new Date(filtroDataAte + 'T23:59:59')
                if (criado > ate) return false
            }
            return true
        })
    }

    const openDeals = filtrarPorData(deals.filter(d => {
        if (d.status !== 'open') return false
        if (busca.trim()) return d.title?.toLowerCase().includes(busca.toLowerCase())
        return true
    }))
    const wonDeals = deals.filter(d => d.status === 'won')
    const closedDeals = deals.filter(d => d.status === 'closed')
    const inProgressDeals = deals.filter(d => d.status === 'in_progress')
    const getDealsByStage = (stageId) => openDeals.filter(d => d.stage_id === stageId)
    const totalFiltrado = filtroDataDe || filtroDataAte
    const totalDealsVisiveis = openDeals.length
    const totalDealsGeral = deals.filter(d => d.status === 'open').length

    const onDragEnd = async (result) => {
        if (!result.destination) return
        const { draggableId, destination } = result
        setDeals(prev => prev.map(d => d.id === draggableId ? { ...d, stage_id: destination.droppableId } : d))
        await api.put(`/deals/${draggableId}`, {
            stage_id: destination.droppableId,
            user_id: user.id,
            user_name: profile?.name
        })
    }

    const handleStatusChange = async (dealId, status, motivo_perda = null) => {
        const payload = { status, user_id: user.id, user_name: profile?.name }
        if (motivo_perda) payload.motivo_perda = motivo_perda
        await api.put(`/deals/${dealId}`, payload)
        setDeals(prev => prev.map(d => d.id === dealId ? { ...d, status, motivo_perda } : d))
    }

    const handleCreate = async () => {
        if (!form.title || !form.stage_id) return
        const cId = getCompanyId()
        const contato = form.contato === '__outro__' ? '' : form.contato
        const payload = {
            ...form,
            contato: contato || null,
            value: form.value || null,
            produto: form.produto || null,
            origem: form.origem || null,
            site: form.site || null,
            segmento: form.segmento || null,
            telefone: form.telefone || null,
            observacoes: form.observacoes || null,
            data_agendamento: form.data_agendamento || null,
            localizacao: form.localizacao || null,
            user_id: user.id,
            user_name: profile?.name,
            company_id: cId
        }
        const { data } = await api.post('/deals', payload)
        setDeals(prev => [...prev, { ...data, profiles: { name: profile?.name } }])
        setForm(emptyForm)
        setShowForm(false)
    }

    const handleDelete = async (dealId) => {
        await api.delete(`/deals/${dealId}`)
        setDeals(prev => prev.filter(d => d.id !== dealId))
    }

    const handleOpenEdit = (deal) => {
        setEditingDeal(deal)
        setEditForm({ title: deal.title || '', value: deal.value || '', stage_id: deal.stage_id || '', produto: deal.produto || '', origem: deal.origem || '', site: deal.site || '', segmento: deal.segmento || '', contato: deal.contato || '', telefone: deal.telefone || '', observacoes: deal.observacoes || '', data_agendamento: deal.data_agendamento || '', localizacao: deal.localizacao || '' })
    }

    const handleSaveEdit = async () => {
        if (!editForm.title || !editForm.stage_id) return
        const contato = editForm.contato === '__outro__' ? '' : editForm.contato
        const payload = {
            ...editForm,
            contato: contato || null,
            value: editForm.value || null,
            produto: editForm.produto || null,
            origem: editForm.origem || null,
            site: editForm.site || null,
            segmento: editForm.segmento || null,
            telefone: editForm.telefone || null,
            observacoes: editForm.observacoes || null,
            data_agendamento: editForm.data_agendamento || null,
            localizacao: editForm.localizacao || null,
            user_id: user.id,
            user_name: profile?.name
        }
        await api.put(`/deals/${editingDeal.id}`, payload)
        setDeals(prev => prev.map(d => d.id === editingDeal.id ? { ...d, ...payload } : d))
        setEditingDeal(null)
    }

    const totalValue = openDeals.reduce((s, d) => s + Number(d.value || 0), 0)
    const empresaAtual = empresas.find(e => e.id === empresaSelecionada)
    const companyId = getCompanyId()

    return (
        <div className="flex flex-col h-full">
            <div className="flex justify-between items-start mb-4 gap-2">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold">Pipeline de Vendas</h1>
                    <p className="text-gray-400 text-sm mt-1">
                        Total em aberto: <span className="text-emerald-400 font-semibold">R$ {totalValue.toLocaleString('pt-BR')}</span>
                        {totalFiltrado && <span className="text-xs text-indigo-400 ml-2">({totalDealsVisiveis} de {totalDealsGeral} leads)</span>}
                    </p>
                </div>
                <div className="flex gap-2">
                    {aba === 'pipeline' && (
                        <button onClick={() => setShowFiltro(!showFiltro)}
                            className={`px-3 py-2 rounded-lg text-sm transition ${totalFiltrado ? 'bg-indigo-600 text-white' : 'bg-gray-700 hover:bg-gray-600'}`}>
                            📅 {totalFiltrado ? 'Filtrado' : 'Filtrar'}
                        </button>
                    )}
                    {aba === 'pipeline' && companyId && (
                        <button onClick={() => setShowConfig(true)} className="bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-sm transition">⚙️ Etapas</button>
                    )}
                    {aba === 'pipeline' && (
                        <button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700 px-3 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap">+ Novo Negócio</button>
                    )}
                </div>
            </div>

            {(aba === 'pipeline' || aba === 'obras' || aba === 'andamento') && (
                <div className="relative mb-4">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                    <input
                        type="text"
                        placeholder={aba === 'obras' ? 'Buscar obra fechada pelo nome...' : aba === 'andamento' ? 'Buscar obra em andamento pelo nome...' : 'Buscar deal pelo nome...'}
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                        className="w-full bg-gray-900 border border-gray-700 rounded-xl pl-9 pr-10 py-2.5 text-sm outline-none focus:ring-2 ring-indigo-500 text-white placeholder-gray-500"
                    />
                    {busca && (
                        <button onClick={() => setBusca('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition">✕</button>
                    )}
                </div>
            )}

            {showFiltro && aba === 'pipeline' && (
                <div className="bg-gray-900 rounded-xl p-4 border border-indigo-800/40 mb-4 flex flex-wrap items-end gap-3">
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">📅 De</label>
                        <input type="date" value={filtroDataDe} onChange={e => setFiltroDataDe(e.target.value)}
                            className="bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 ring-indigo-500" />
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">📅 Até</label>
                        <input type="date" value={filtroDataAte} onChange={e => setFiltroDataAte(e.target.value)}
                            className="bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 ring-indigo-500" />
                    </div>
                    {totalFiltrado && (
                        <button onClick={() => { setFiltroDataDe(''); setFiltroDataAte('') }}
                            className="bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-sm transition text-gray-300">
                            ✕ Limpar filtro
                        </button>
                    )}
                    {totalFiltrado && (
                        <span className="text-xs text-indigo-400 bg-indigo-900/30 px-3 py-2 rounded-lg">
                            {totalDealsVisiveis} lead{totalDealsVisiveis !== 1 ? 's' : ''} no período
                        </span>
                    )}
                </div>
            )}

            <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={() => { setAba('pipeline'); setBusca('') }} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${aba === 'pipeline' ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>🔀 Pipeline</button>
                {tipoPipeline === 'pavimentacao' && (
                    <button onClick={() => { setAba('andamento'); setBusca('') }} className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${aba === 'andamento' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                        🚧 Em Andamento
                        {inProgressDeals.length > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${aba === 'andamento' ? 'bg-blue-500' : 'bg-gray-700'}`}>{inProgressDeals.length}</span>}
                    </button>
                )}
                {tipoPipeline === 'pavimentacao' && (
                    <button onClick={() => { setAba('obras'); setBusca('') }} className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${aba === 'obras' ? 'bg-orange-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                        🏗️ Obras Fechadas
                        {closedDeals.length > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${aba === 'obras' ? 'bg-orange-500' : 'bg-gray-700'}`}>{closedDeals.length}</span>}
                    </button>
                )}
                <button onClick={() => { setAba('ganhos'); setBusca('') }} className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${aba === 'ganhos' ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                    🏆 Ganhos
                    {wonDeals.length > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${aba === 'ganhos' ? 'bg-emerald-500' : 'bg-gray-700'}`}>{wonDeals.length}</span>}
                </button>
            </div>

            {isMaster && (
                <div className="flex flex-wrap items-center gap-3 mb-4 bg-gray-900 rounded-xl p-3 border border-gray-800">
                    <span className="text-sm text-gray-400">🏢 Empresa:</span>
                    <select className="bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 ring-indigo-500 flex-1 min-w-0" value={empresaSelecionada || ''} onChange={e => setEmpresaSelecionada(e.target.value || null)}>
                        <option value="">Todas as empresas</option>
                        {empresas.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                    {empresaSelecionada && <button onClick={() => setEmpresaSelecionada(null)} className="text-xs text-gray-500 hover:text-white transition">✕</button>}
                    {empresaAtual && <span className="text-xs text-indigo-400 bg-indigo-900/30 px-3 py-1 rounded-full">🏢 {empresaAtual.name}</span>}
                </div>
            )}

            {showForm && <FormNegocio title="Novo Negócio" form={form} setForm={setForm} onSave={handleCreate} onCancel={() => { setShowForm(false); setForm(emptyForm) }} stages={stages} produtos={produtos} contatos={contatos} saveLabel="Criar" />}
            {editingDeal && <FormNegocio title="✏️ Editar Negócio" form={editForm} setForm={setEditForm} onSave={handleSaveEdit} onCancel={() => setEditingDeal(null)} stages={stages} produtos={produtos} contatos={contatos} saveLabel="Salvar" />}
            {showConfig && <ConfigurarPipeline stages={stages} companyId={companyId} onClose={() => setShowConfig(false)} onUpdate={carregarStages} />}

            {aba === 'andamento' && tipoPipeline === 'pavimentacao' ? (
                <ObrasAndamentoView deals={deals} onStatusChange={handleStatusChange} busca={busca} onEdit={handleOpenEdit} />
            ) : aba === 'obras' && tipoPipeline === 'pavimentacao' ? (
                <ObrasView deals={deals} onStatusChange={handleStatusChange} onReabrir={(id) => handleStatusChange(id, 'open')} busca={busca} />
            ) : aba === 'ganhos' ? (
                <GanhosView deals={deals} onStatusChange={handleStatusChange} mesGanhos={mesGanhos} setMesGanhos={setMesGanhos} />
            ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="-mx-4 md:mx-0 px-4 md:px-0 overflow-x-auto">
                        <div className="flex gap-3 pb-4" style={{ minWidth: `${stages.length * 220}px` }}>
                            {stages.map(stage => (
                                <div key={stage.id} className="flex-shrink-0 w-52 md:w-64">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }}></div>
                                        <span className="font-semibold text-sm truncate">{stage.name}</span>
                                        <span className="ml-auto bg-gray-800 text-gray-400 text-xs px-2 py-0.5 rounded-full flex-shrink-0">{getDealsByStage(stage.id).length}</span>
                                    </div>
                                    <Droppable droppableId={stage.id}>
                                        {(provided, snapshot) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}
                                                className={`min-h-32 rounded-lg p-2 transition ${snapshot.isDraggingOver ? 'bg-indigo-900/30 border border-indigo-500/50' : 'bg-gray-900/50'}`}>
                                                {getDealsByStage(stage.id).map((deal, index) => (
                                                    <DealCard key={deal.id} deal={deal} index={index} onDelete={handleDelete} onStatusChange={handleStatusChange} onEdit={handleOpenEdit} tipoPipeline={tipoPipeline} />
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            ))}
                        </div>
                    </div>
                </DragDropContext>
            )}
        </div>
    )
}